import {
  getSessionCompositionCounts,
  type SessionCompositionEntry,
  type SessionCompositionOutcome,
} from "@/shared/domain/sessions";

import { sessionCompositionEntryReason } from "./sessionCompositionEntryReason";
import styles from "./SessionCompositionStatus.module.css";

/** Count every selected file by what happened to it, then name each file that is not
 * confirmed in the session and why. */
export function SessionCompositionStatus({
  outcome,
}: {
  outcome?: SessionCompositionOutcome;
}) {
  if (!outcome) {
    return null;
  }
  const counts = getSessionCompositionCounts(outcome);
  const unresolved = outcome.entries.filter(
    (entry) => entry.status !== "included",
  );
  return (
    <section aria-label="File addition results" className={styles.status}>
      <dl className={styles.counts} aria-live="polite">
        <div>
          <dt>Included</dt>
          <dd>{counts.included}</dd>
        </div>
        <div>
          <dt>Not added</dt>
          <dd>{counts.refused}</dd>
        </div>
        <div>
          <dt>Unconfirmed</dt>
          <dd>{counts.unknown}</dd>
        </div>
        <div>
          <dt>Not sent</dt>
          <dd>{counts.notAttempted}</dd>
        </div>
      </dl>
      <StopReason outcome={outcome} />
      <UnresolvedFiles entries={unresolved} />
    </section>
  );
}

function StopReason({ outcome }: { outcome: SessionCompositionOutcome }) {
  if (outcome.reconciliationError !== undefined) {
    return (
      <p role="status">
        Could not read the session’s file list, so the unconfirmed files were
        not checked.
      </p>
    );
  }
  if (outcome.stopCause?.kind === "stopped") {
    return <p role="status">Stopped before the remaining files were sent.</p>;
  }
  if (outcome.stopCause) {
    return (
      <p role="status">
        The session did not report back on every file we sent.
      </p>
    );
  }
  return null;
}

function UnresolvedFiles({
  entries,
}: {
  entries: readonly SessionCompositionEntry[];
}) {
  if (entries.length === 0) {
    return null;
  }
  return (
    <ul className={styles.files}>
      {entries.map((entry) => (
        <li key={entry.originalIndex}>
          <strong>{entry.declaration.file_id}</strong>:{" "}
          {sessionCompositionEntryReason(entry)}
        </li>
      ))}
    </ul>
  );
}
